import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, DateTime } from '~/components/Form';
import { t } from '~/helpers';
import {
  storeData,
  updateData,
} from '~/store/modules/recebimentoAreiaNf/actions';

const FormContent = styled.div`
  .MuiFormHelperText-root.Mui-error {
    margin-top: 3px;
  }
`;

const MAX_QUANTIDADE_SACOS = 9999;

function normalizeQuantidadeSacos(value) {
  const onlyNumbers = String(value || '').replace(/\D/g, '');
  if (!onlyNumbers) return '';

  return String(Math.min(Number(onlyNumbers), MAX_QUANTIDADE_SACOS));
}

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();
  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.recebimentoAreiaNf
  );

  const initials = useMemo(() => (create ? data : { ...find, ...data }), [
    create,
    data,
    find,
  ]);

  const [idposto, setIdposto] = useState(initials?.idposto || null);
  const [estoque, setEstoque] = useState(null);
  const [quantidade, setQuantidade] = useState('');
  const [pesoUnitario, setPesoUnitario] = useState('');
  const [itens, setItens] = useState([]);
  const [estoqueKey, setEstoqueKey] = useState(0);
  const initialsHydratedRef = useRef(null);
  const initialsKey = useMemo(
    () =>
      create
        ? 'create'
        : String(initials?.idrecebimentoareianf || 'empty-update'),
    [create, initials]
  );

  const totalPesoItens = useMemo(
    () =>
      itens.reduce(
        (total, item) =>
          total + Number(item.pesototalkgrecebimentoareianfitem || 0),
        0
      ),
    [itens]
  );

  const estoqueParams = useMemo(
    () =>
      idposto
        ? {
            wherefield: ['idposto', 'silotipoareia'],
            valuewherefield: [idposto, 'S'],
            typewherefield: ['EQUALS', 'NEQUALS'],
            orderfield: 'descricaotipoareia',
            ordination: 'asc',
          }
        : {},
    [idposto]
  );

  function getDescricaoEstoque(item) {
    if (!item) return '';
    if (item.estoqueAreiaVO) {
      return (
        item.estoqueAreiaVO.nomepostotipoareia ||
        item.estoqueAreiaVO.descricaotipoareia
      );
    }
    return item.nomepostotipoareia || item.descricaotipoareia;
  }

  function handleAddItem() {
    if (!idposto) {
      toast.error('Informe o posto antes dos itens.');
      return;
    }
    if (!estoque || !estoque.idpostotipoareia) {
      toast.error('Informe o tipo de areia.');
      return;
    }

    const qtd = Number(quantidade);
    const peso = Number(pesoUnitario || estoque.pesounitariokgtipoareia);

    if (!qtd || qtd <= 0) {
      toast.error('Quantidade invalida.');
      return;
    }
    if (qtd > MAX_QUANTIDADE_SACOS) {
      toast.error('Quantidade de sacos nao pode ser superior a 9999.');
      return;
    }
    if (!peso || peso <= 0) {
      toast.error('Peso unitario invalido.');
      return;
    }
    if (
      itens.some((item) => item.idpostotipoareia === estoque.idpostotipoareia)
    ) {
      toast.error('Este tipo de areia ja foi incluido.');
      return;
    }

    setItens([
      ...itens,
      {
        idpostotipoareia: estoque.idpostotipoareia,
        // descricao: getDescricaoEstoque(estoque),
        quantidadesacosrecebimentoareianfitem: qtd,
        pesounitariokgrecebimentoareianfitem: peso,
        pesototalkgrecebimentoareianfitem: qtd * peso,
      },
    ]);
    setEstoque(null);
    setQuantidade('');
    setPesoUnitario('');
    reference.current.setFieldValue('idpostotipoareiaitem', null);
    setEstoqueKey((key) => key + 1);
  }

  function handleRemoveItem(idpostotipoareia) {
    setItens(
      itens.filter((item) => item.idpostotipoareia !== idpostotipoareia)
    );
  }

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        cnpjdistribuidoranferecebimentoareianf: Yup.string()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
        numeronfrecebimentoareianf: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20)),
        placactrecebimentoareianf: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        numeropedidorecebimentoareianf: Yup.string()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(15, t('s.geral.validacao.tamanhomaximo').replace('%s', 15)),
        quantidaderecebimentoareianf: Yup.number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null ? undefined : value
          )
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .integer(t('s.geral.validacao.campoinvalido'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .moreThan(0, t('s.geral.validacao.valorminimo').replace('%s', 1)),
        dataemissaonfrecebimentoareianf: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        datachegadarecebimentoareianf: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        idusuariorecebimentoareianf: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio')),
      });

      await schema.validate(param, { abortEarly: false });

      if (itens.length === 0) {
        toast.error('Informe ao menos um item de areia.');
        return;
      }

      const itensRecebimento = itens.map((item) => ({
        idrecebimentoareianfitem: item.idrecebimentoareianfitem,
        idrecebimentoareianf: item.idrecebimentoareianf,
        idcliente: item.idcliente,
        idposto: item.idposto,
        idpostotipoareia: item.idpostotipoareia,
        quantidadesacosrecebimentoareianfitem:
          item.quantidadesacosrecebimentoareianfitem,
        pesounitariokgrecebimentoareianfitem:
          item.pesounitariokgrecebimentoareianfitem,
        pesototalkgrecebimentoareianfitem:
          item.pesototalkgrecebimentoareianfitem,
      }));

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        idposto: create ? Number(param.idposto) : initials.idposto,
        cnpjdistribuidoranferecebimentoareianf:
          param.cnpjdistribuidoranferecebimentoareianf || null,
        numeronfrecebimentoareianf: param.numeronfrecebimentoareianf.trim(),
        placactrecebimentoareianf: param.placactrecebimentoareianf.trim(),
        numeropedidorecebimentoareianf: param.numeropedidorecebimentoareianf.trim(),
        quantidaderecebimentoareianf: Number(
          param.quantidaderecebimentoareianf
        ),
        idusuariorecebimentoareianf: Number(param.idusuariorecebimentoareianf),
        itens: itensRecebimento,
      };

      delete objSend.idpostotipoareiaitem;

      if (create) {
        dispatch(storeData(objSend, params));
      } else {
        dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (
      loading ||
      !reference.current ||
      initialsHydratedRef.current === initialsKey
    )
      return;

    initialsHydratedRef.current = initialsKey;
    reference.current.setData(initials);
    setIdposto(initials?.idposto || null);
    setItens(
      (initials?.itens || []).map((item) => ({
        ...item,
        descricao: getDescricaoEstoque(item),
      }))
    );
  }, [loading, initials, initialsKey, reference]);

  return loading ? (
    <div style={{ height: '14rem', width: '60rem' }}>
      <Spinner loading />
    </div>
  ) : (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <FormContent>
        <Grid
          container
          spacing={2}
          style={{ width: '60rem', maxWidth: '100%' }}
        >
          <Grid item xs={12} md={6}>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postos"
              unique="idposto"
              title={t('s.menu.configuracao.posto')}
              disabled={!create}
              getValue={(value) => {
                const novoPosto = value ? value.idposto : null;
                const postoAlterado = novoPosto !== idposto;
                setIdposto(novoPosto);
                reference.current.setFieldValue('idposto', novoPosto);
                if (postoAlterado) {
                  reference.current.setFieldValue(
                    'cnpjdistribuidoranferecebimentoareianf',
                    null
                  );
                  reference.current.setFieldValue(
                    'idusuariorecebimentoareianf',
                    null
                  );
                  reference.current.setFieldValue('idpostotipoareiaitem', null);
                  setItens([]);
                  setEstoque(null);
                  setQuantidade('');
                  setPesoUnitario('');
                  setEstoqueKey((key) => key + 1);
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              key={`distribuidora-${idposto || 'sem-posto'}`}
              name="cnpjdistribuidoranferecebimentoareianf"
              label="cnpjnomedistribuidoranfe"
              url="distribuidorasnfes"
              unique="cnpjdistribuidoranfe"
              title={t('s.geral.fornecedor')}
              disabled={!idposto}
              getValue={(value) => {
                reference.current.setFieldValue(
                  'cnpjdistribuidoranferecebimentoareianf',
                  value ? value.cnpjdistribuidoranfe : null
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Input
              name="numeronfrecebimentoareianf"
              label={t('s.configuracao.tanquefisico.numerosondatanquefisico')}
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 20 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Input
              name="placactrecebimentoareianf"
              label={t('s.operacao.recebimento.placactdescarga')}
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 10 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Input
              name="numeropedidorecebimentoareianf"
              label={t('s.relatorio.descarga.pedido')}
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 15 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Input
              name="quantidaderecebimentoareianf"
              label={t('s.geral.pesodeclarado') + ` Kg`}
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTime
              name="dataemissaonfrecebimentoareianf"
              label={t('s.operacao.notafiscalrecebida.emissaonfe')}
              time
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTime
              name="datachegadarecebimentoareianf"
              label={t('s.geral.datachegada')}
              time
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Autocomplete
              name="idusuariorecebimentoareianf"
              label="nomeusuario"
              url="usuarios"
              unique="idusuario"
              title={t('s.geral.executante')}
              //disableClearable
              disabled={!idposto}
              params={{
                filterusrposto: idposto,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="observacaorecebimentoareianf"
              label={t('s.geral.observacao')}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 255 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('s.geral.itensrecebidos')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={5}>
            <Autocomplete
              key={`estoque-${idposto || 'sem-posto'}-${estoqueKey}`}
              name="idpostotipoareiaitem"
              label="descricaotipoareia"
              url="estoqueareia"
              unique="idpostotipoareia"
              title={t('s.cadastro.pitstop.tipoareia')}
              params={estoqueParams}
              disabled={!idposto}
              getValue={(value) => {
                setEstoque(value);
                setPesoUnitario(value ? value.pesounitariokgtipoareia : '');
              }}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label={t('s.geral.sacos')}
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              value={quantidade}
              onChange={(event) =>
                setQuantidade(normalizeQuantidadeSacos(event.target.value))
              }
              inputProps={{ min: 1, max: MAX_QUANTIDADE_SACOS, step: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label={t('s.geral.pesounitario') + ` Kg`}
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              value={pesoUnitario}
              onChange={(event) => setPesoUnitario(event.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              type="button"
              color="primary"
              variant="contained"
              fullWidth
              onClick={handleAddItem}
            >
              {t('s.geral.botaoincluir')}
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('s.cadastro.pitstop.tipoareia')}</TableCell>
                    <TableCell align="right">{t('s.geral.sacos')}</TableCell>
                    <TableCell align="right">
                      {t('s.geral.pesounitario')} Kg
                    </TableCell>
                    <TableCell align="right">
                      {t('s.geral.pesototal')} Kg
                    </TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.map((item) => (
                    <TableRow key={item.idpostotipoareia}>
                      <TableCell>{item.descricao}</TableCell>
                      <TableCell align="right">
                        {item.quantidadesacosrecebimentoareianfitem}
                      </TableCell>
                      <TableCell align="right">
                        {item.pesounitariokgrecebimentoareianfitem}
                      </TableCell>
                      <TableCell align="right">
                        {item.pesototalkgrecebimentoareianfitem}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveItem(item.idpostotipoareia)
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell>
                      <strong>{t('s.geral.total')}</strong>
                    </TableCell>
                    <TableCell align="right" />
                    <TableCell align="right" />
                    <TableCell align="right">
                      <strong>{totalPesoItens}</strong>
                    </TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      </FormContent>
    </Form>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
